/**
 * Products Service
 * Handles all product-related operations
 */

const BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || '/api')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

// Helper function to normalize image URLs
const normalizeImageUrl = (product) => {
  if (product.image_url) {
    if (product.image_url.startsWith('http')) {
      return product.image_url;
    }
    if (product.image_url.startsWith('/uploads/')) {
      return `${BASE_URL.replace('/api', '')}${product.image_url}`;
    }
  }
  return product.image_url;
};

/**
 * Get all products with optional filtering
 */
export const getProducts = async (category = null, subcategory = null) => {
  try {
    let url = `${BASE_URL}/products`;
    const params = new URLSearchParams();
    
    if (category) params.append('category', category);
    if (subcategory) params.append('subcategory', subcategory);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.status}`);
    }

    const products = await response.json();
    
    // Normalize image URLs
    return products.map(product => ({
      ...product,
      image_url: normalizeImageUrl(product),
      discounted_price: product.discount_percent > 0 
        ? Number((product.price - (product.price * (product.discount_percent / 100))).toFixed(2))
        : null,
    }));
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Get a single product by ID
 */
export const getProductById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/products/${id}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product not found');
      }
      throw new Error(`Failed to fetch product: ${response.status}`);
    }

    const product = await response.json();
    product.image_url = normalizeImageUrl(product);
    
    if (product.discount_percent > 0) {
      product.discounted_price = Number((product.price - (product.price * (product.discount_percent / 100))).toFixed(2));
    }
    
    return product;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

/**
 * Get products by category
 */
export const getProductsByCategory = async (category) => {
  try {
    const response = await fetch(`${BASE_URL}/products/category/${encodeURIComponent(category)}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.status}`);
    }

    const products = await response.json();
    return products.map(product => ({
      ...product,
      image_url: normalizeImageUrl(product),
    }));
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};

const productServiceExports = {
  getProducts,
  getProductById,
  getProductsByCategory,
};

export default productServiceExports;
